import React, { useMemo } from 'react';
import { FloatingUserCell } from "./FloatingUserCell";
import {
  BorderDayCell,
  ROW_HEIGHT,
  USER_ROW_HEIGHT_MOBILE,
  WallPlannerRow,
  WEEK_WIDTH,
  WeekCell
} from '../WallPlannerStructuralComponents';
import styled from "styled-components";
import { UserAvatarAndName } from '@shared/ui/molecules/UserAvatarAndName';
import { BasicUserInfo, Birthday, Permission } from '@shared/models/user.models';
import { useSelector } from "react-redux";
import { selectCurrentUser, selectIconPack } from '@shared/store/auth.duck';
import { smallTablet } from '@shared/ui/atoms/MediaQueries';
import { WithWeeksInCurrentCalendarMonth } from '@features/whereabouts/hooks/WithWeekInCurrentCalendarMonth';
import { Period } from '@features/whereabouts/models/movements.models';
import { CheckLineReport } from "@shared/hooks/management-structure/CheckLineReport";
import { IconTypes } from '@shared/ui/atoms/icon/Icon';
import { ActivityBlockV2 } from "./WallPlannerActivityBlocksV2";
import { UsersWorkBlock } from '@features/wall-planner/wall-planner.models';
import { FloatingBirthdayIcon } from "./FloatingBirthdayIcon";

interface Props {
  user: BasicUserInfo;
  whereaboutsDays: UsersWorkBlock[];
  children?: any;
  id?: string;
}

export function WallPlannerUserRowV2(props: Props) {
  const {user, whereaboutsDays, children} = props;
  const currentUser = useSelector(selectCurrentUser);
  const iconPack = useSelector(selectIconPack);
  const weeksInMonth = WithWeeksInCurrentCalendarMonth();
  const holidayBookingEnabled = currentUser?.companyEntity.enableHolidayBooking;

  const birthday: Birthday | undefined = (currentUser?.companyEntity.showBirthdays && user?.birthdayDay && user.birthdayMonth) ? {day: user.birthdayDay, month: user.birthdayMonth} : undefined

  const isLineReport = CheckLineReport(currentUser, user?.userId);

  const canUserViewPopup = useMemo(() => {
    if (!window.location.href.includes('/holidays')) {
      return false;
    }

    if (!holidayBookingEnabled) {
      return false;
    }

    if (currentUser?.role === Permission.CompanyAdmin) {
      return true;
    }

    if (currentUser?.role === Permission.TeamAdmin && currentUser.teamEntity !== null && currentUser.teamEntity.id === user?.teamId) {
      return true;
    }

    return isLineReport;
  }, [holidayBookingEnabled, currentUser?.role, currentUser?.teamEntity, user?.teamId, isLineReport]);

  if (!weeksInMonth || weeksInMonth.length === 0) {
    return null;
  }

  const firstDay = weeksInMonth[0];

  return (
    <WallPlannerUserRowContainer weekCount={weeksInMonth.length} id={props?.id ?? ''}>
      <FloatingUserCell>
        {children && <>
            {children}
        </>}
        {user && !children && <>
          <UserAvatarAndName colour={currentUser?.id === user.userId ? 'blue' : 'darkBlue'}
                             onHoverIcon={canUserViewPopup ? IconTypes.Holiday : undefined}
                             basicUserInfo={user}
                             userId={user.userId}
                             showUserHoverInfo={true}
                             showHolidayContentOnHover={true}
                             firstName={user.firstName}
                             lastName={user.lastName}
                             jobTitle={user.jobTitle}/>
        </>}

      </FloatingUserCell>
      {weeksInMonth.map((timeFrame, k) => (
        <WeekCell key={k}>
          <BorderDayCell/>
          <BorderDayCell/>
          <BorderDayCell/>
          <BorderDayCell/>
          <BorderDayCell/>
          <BorderDayCell weekend={true}/>
          <BorderDayCell weekend={true}/>
        </WeekCell>
      ))}

      <UserActivitiesContainer>
        {whereaboutsDays?.map((whereabouts: UsersWorkBlock, key) => (
          <React.Fragment key={key}>
            {(whereabouts?.amStatusId !== whereabouts?.pmStatusId) ? <HalfDayColumn>
                <ActivityBlockV2 iconPack={iconPack} style={{top: -16, height: 14}} whereabouts={whereabouts} sideOfDay={Period.AM} firstDay={firstDay} />
                <ActivityBlockV2 iconPack={iconPack} style={{top: 2, height: 14}} whereabouts={whereabouts} sideOfDay={Period.PM} firstDay={firstDay} />
              </HalfDayColumn> :
              <ActivityBlockV2 iconPack={iconPack} whereabouts={whereabouts} firstDay={firstDay} sideOfDay={Period.AllDay} />
            }
          </React.Fragment>
        ))}

        {birthday && <FloatingBirthdayIcon birthday={birthday} monthStartDate={firstDay} weeksInMonth={weeksInMonth.length} />}
      </UserActivitiesContainer>

    </WallPlannerUserRowContainer>
  )
}


const WallPlannerUserRowContainer = styled(WallPlannerRow)<any>`
  height: ${USER_ROW_HEIGHT_MOBILE}px;
  width: 100%;
  transition: filter 0.15s;
  &:hover {
    filter: brightness(0.97);
  }
  @media (${smallTablet}) {
    height: ${ROW_HEIGHT}px;
    left: 0;
  }
`

const UserActivitiesContainer = styled.div`
  position: absolute;
  left: -${WEEK_WIDTH}px;
  top: 40px;
  @media (${smallTablet}) {
    left: 0;
    top: 28px;
  }
`

const HalfDayColumn = styled.div`
  display: flex;
  flex-direction: column;
`
